import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableRow, Paper,
    TableHead, Button, TextField, Dialog, DialogActions, DialogContent,
    DialogTitle, Autocomplete, Grid, Box, IconButton, Tooltip,
    TablePagination, Typography, InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Search as SearchIcon,
    Business as BusinessIcon
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';

function GestionUnidadNegocioSucursal() {
    const server = process.env.REACT_APP_API_ROUTE;
    const { user } = useUser();
    const { tienePermiso } = useAccesos();

    // Función helper para formatear el usuario a 20 caracteres
    const formatUser = (username) => {
        if (!username) username = 'ADMIN';
        if (username.length >= 20) return username.substring(0, 20);
        return username.padEnd(20, ' ');
    };

    // Estados principales
    const [registros, setRegistros] = useState([]);
    const [unidadesNegocio, setUnidadesNegocio] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [empresas, setEmpresas] = useState([]);

    // Estados de control
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [editData, setEditData] = useState({});
    const [openDialog, setOpenDialog] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Estado del formulario
    const [newRegistro, setNewRegistro] = useState({
        idUnidadNegocio: null,
        idSucursal: null
    });

    // Estados de filtros
    const [filtroEmpresa, setFiltroEmpresa] = useState(null);
    const [filtroSucursal, setFiltroSucursal] = useState(null);
    const [filtroUnidadNegocio, setFiltroUnidadNegocio] = useState(null);

    useEffect(() => {
        fetchRegistros();
        fetchCatalogos();
    }, []);

    const fetchRegistros = async () => {
        try {
            const response = await fetch(`${server}/CtbUnidadNegocioXSucursals`, {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });

            if (!response.ok) throw new Error('Error al cargar registros');

            const data = await response.json();
            const registrosData = data.$values || data || [];

            console.log('✅ Registros cargados:', registrosData.length);
            setRegistros(registrosData);
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', 'No se pudo cargar los registros', 'error');
        }
    };

    const fetchCatalogos = async () => {
        try {
            const [unidadesRes, sucursalesRes, empresasRes] = await Promise.all([
                fetch(`${server}/CtbUnidadNegocios`, { headers: { 'Authorization': `Bearer ${user?.token}` } }),
                fetch(`${server}/MgSucursales`, { headers: { 'Authorization': `Bearer ${user?.token}` } }),
                fetch(`${server}/MgEmpresas`, { headers: { 'Authorization': `Bearer ${user?.token}` } })
            ]);

            const [unidadesData, sucursalesData, empresasData] = await Promise.all([
                unidadesRes.json(),
                sucursalesRes.json(),
                empresasRes.json()
            ]);

            setUnidadesNegocio(unidadesData.$values || unidadesData || []);
            setSucursales(sucursalesData.$values || sucursalesData || []);
            setEmpresas(empresasData.$values || empresasData || []);
        } catch (error) {
            console.error('Error al cargar catálogos:', error);
        }
    };

    const handleAddNew = async () => {
        try {
            if (!newRegistro.idUnidadNegocio || !newRegistro.idSucursal) {
                Swal.fire('Advertencia', 'Debe completar todos los campos requeridos', 'warning');
                return;
            }

            const dataToSend = {
                ...newRegistro,
                ingresadoPor: formatUser(user?.codigoUsuario || 'ADMIN'),
                fechaIngreso: new Date().toISOString(),
                modificacion: 1
            };

            const response = await fetch(`${server}/CtbUnidadNegocioXSucursals`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.token}`
                },
                body: JSON.stringify(dataToSend)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
                throw new Error(errorData.message || 'Error al crear');
            }

            await Swal.fire('Éxito', 'Registro creado correctamente', 'success');
            setOpenDialog(false);
            setNewRegistro({
                idUnidadNegocio: null,
                idSucursal: null
            });
            fetchRegistros();
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleEdit = (registro) => {
        setEditingId(registro.id);
        setEditData({ ...registro });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditData({});
    };

    const handleSaveEdit = async () => {
        try {
            if (!editData.idUnidadNegocio || !editData.idSucursal) {
                Swal.fire('Advertencia', 'Debe completar todos los campos requeridos', 'warning');
                return;
            }

            const dataToSend = {
                ...editData,
                modificadoPor: formatUser(user?.codigoUsuario || 'ADMIN'),
                fechaModificacion: new Date().toISOString()
            };

            const response = await fetch(
                `${server}/CtbUnidadNegocioXSucursals/${editData.id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user?.token}`
                    },
                    body: JSON.stringify(dataToSend)
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
                throw new Error(errorData.message || 'Error al actualizar');
            }

            await Swal.fire('Éxito', 'Registro actualizado correctamente', 'success');
            setEditingId(null);
            setEditData({});
            fetchRegistros();
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Está seguro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(
                    `${server}/CtbUnidadNegocioXSucursals/${id}/${encodeURIComponent(formatUser(user?.codigoUsuario || 'ADMIN'))}`,
                    {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${user?.token}` }
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
                    throw new Error(errorData.message || 'Error al eliminar');
                }

                await Swal.fire('Eliminado', 'Registro eliminado correctamente', 'success');
                fetchRegistros();
            } catch (error) {
                console.error('Error:', error);
                Swal.fire('Error', error.message, 'error');
            }
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getNombreUnidadNegocio = (idUnidad) => {
        return unidadesNegocio.find(u => u.id === idUnidad)?.unidadNegocio || 'N/A';
    };

    const getNombreSucursal = (idSucursal) => {
        return sucursales.find(s => s.idSucursal === idSucursal)?.nombreSucursal || 'N/A';
    };

    const getNombreEmpresa = (idSucursal) => {
        const sucursal = sucursales.find(s => s.idSucursal === idSucursal);
        if (!sucursal) return 'N/A';
        const empresa = empresas.find(e => e.idEmpresa === sucursal.idEmpresa);
        return empresa?.nombreCompleto || 'N/A';
    };

    const matchesSearch = (registro) => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();

        const unidad = getNombreUnidadNegocio(registro.idUnidadNegocio);
        const sucursal = getNombreSucursal(registro.idSucursal);
        const empresa = getNombreEmpresa(registro.idSucursal);

        return (
            unidad.toLowerCase().includes(searchLower) ||
            sucursal.toLowerCase().includes(searchLower) ||
            empresa.toLowerCase().includes(searchLower)
        );
    };

    const matchesFilters = (registro) => {
        if (filtroEmpresa) {
            const sucursal = sucursales.find(s => s.idSucursal === registro.idSucursal);
            if (sucursal && sucursal.idEmpresa !== filtroEmpresa.idEmpresa) return false;
        }

        if (filtroSucursal && registro.idSucursal !== filtroSucursal.idSucursal) return false;
        if (filtroUnidadNegocio && registro.idUnidadNegocio !== filtroUnidadNegocio.id) return false;

        return true;
    };

    const filteredRegistros = registros.filter(r => matchesSearch(r) && matchesFilters(r));
    const paginatedRegistros = filteredRegistros.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const renderRegistroRow = (registro) => {
        const isEditing = editingId === registro.id;

        return (
            <TableRow key={registro.id} hover>
                <TableCell>
                    {isEditing ? (
                        <Autocomplete
                            size="small"
                            value={unidadesNegocio.find(u => u.id === editData.idUnidadNegocio) || null}
                            onChange={(e, newValue) => setEditData({ ...editData, idUnidadNegocio: newValue?.id || null })}
                            options={unidadesNegocio}
                            getOptionLabel={(option) => option.unidadNegocio || ''}
                            renderInput={(params) => <TextField {...params} />}
                            sx={{ minWidth: 200 }}
                        />
                    ) : (
                        getNombreUnidadNegocio(registro.idUnidadNegocio)
                    )}
                </TableCell>

                <TableCell>
                    {getNombreEmpresa(registro.idSucursal)}
                </TableCell>

                <TableCell>
                    {isEditing ? (
                        <Autocomplete
                            size="small"
                            value={sucursales.find(s => s.idSucursal === editData.idSucursal) || null}
                            onChange={(e, newValue) => setEditData({ ...editData, idSucursal: newValue?.idSucursal || null })}
                            options={sucursales}
                            getOptionLabel={(option) => option.nombreSucursal || ''}
                            renderInput={(params) => <TextField {...params} />}
                            sx={{ minWidth: 150 }}
                        />
                    ) : (
                        getNombreSucursal(registro.idSucursal)
                    )}
                </TableCell>

                <TableCell align="center">
                    {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Guardar">
                                <IconButton size="small" color="primary" onClick={handleSaveEdit} disabled={!tienePermiso('Editar Unidad Negocio')}>
                                    <SaveIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancelar">
                                <IconButton size="small" color="secondary" onClick={handleCancelEdit}>
                                    <CancelIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Editar">
                                <IconButton size="small" color="primary" onClick={() => handleEdit(registro)} disabled={!tienePermiso('Editar Unidad Negocio')}>
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                                <IconButton size="small" color="error" onClick={() => handleDelete(registro.id)} disabled={!tienePermiso('Eliminar Unidad Negocio')}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </TableCell>
            </TableRow>
        );
    };

    return (
        <div className="right_col" role="main">
            <div className="page-title">
                <div className="title_left">
                    <h3>Gestión de Unidades de Negocio por Sucursal</h3>
                </div>
            </div>

            <div className="clearfix"></div>

            <div className="row">
                <div className="col-md-12 col-sm-12">
                    <div className="x_panel">
                        <div className="x_title">
                            <h2>Unidades de Negocio x Sucursal</h2>
                            <div className="clearfix"></div>
                        </div>
                        <div className="x_content">
                            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                                <TextField
                                    size="small"
                                    placeholder="Buscar..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{ minWidth: 250 }}
                                />

                                <Autocomplete
                                    size="small"
                                    value={filtroEmpresa}
                                    onChange={(e, newValue) => setFiltroEmpresa(newValue)}
                                    options={empresas}
                                    getOptionLabel={(option) => option.nombreCompleto || ''}
                                    renderInput={(params) => <TextField {...params} label="Filtrar por Empresa" />}
                                    sx={{ minWidth: 200 }}
                                />

                                <Autocomplete
                                    size="small"
                                    value={filtroSucursal}
                                    onChange={(e, newValue) => setFiltroSucursal(newValue)}
                                    options={sucursales}
                                    getOptionLabel={(option) => option.nombreSucursal || ''}
                                    renderInput={(params) => <TextField {...params} label="Filtrar por Sucursal" />}
                                    sx={{ minWidth: 200 }}
                                />

                                <Autocomplete
                                    size="small"
                                    value={filtroUnidadNegocio}
                                    onChange={(e, newValue) => setFiltroUnidadNegocio(newValue)}
                                    options={unidadesNegocio}
                                    getOptionLabel={(option) => option.unidadNegocio || ''}
                                    renderInput={(params) => <TextField {...params} label="Filtrar por Unidad" />}
                                    sx={{ minWidth: 200 }}
                                />

                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setOpenDialog(true)}
                                    sx={{ ml: 'auto' }}
                                    disabled={!tienePermiso('Editar Unidad Negocio')}
                                >
                                    NUEVO REGISTRO
                                </Button>
                            </Box>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#1976d2' }}>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unidad de Negocio</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Empresa</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Sucursal</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedRegistros.map(registro => renderRegistroRow(registro))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={filteredRegistros.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                labelRowsPerPage="Filas por página:"
                            />

                            {filteredRegistros.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography color="textSecondary">
                                        {searchTerm ? 'No se encontraron resultados' : 'No hay registros'}
                                    </Typography>
                                </Box>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Dialog para nuevo registro */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon />
                        <Typography variant="h6">Nueva Unidad de Negocio por Sucursal</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                size="small"
                                value={unidadesNegocio.find(u => u.id === newRegistro.idUnidadNegocio) || null}
                                onChange={(e, newValue) => setNewRegistro({ ...newRegistro, idUnidadNegocio: newValue?.id || null })}
                                options={unidadesNegocio}
                                getOptionLabel={(option) => option.unidadNegocio || ''}
                                renderInput={(params) => <TextField {...params} label="Unidad de Negocio *" />}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                size="small"
                                value={sucursales.find(s => s.idSucursal === newRegistro.idSucursal) || null}
                                onChange={(e, newValue) => setNewRegistro({ ...newRegistro, idSucursal: newValue?.idSucursal || null })}
                                options={sucursales}
                                getOptionLabel={(option) => option.nombreSucursal || ''}
                                renderInput={(params) => <TextField {...params} label="Sucursal *" />}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleAddNew}
                        disabled={!newRegistro.idUnidadNegocio || !newRegistro.idSucursal || !tienePermiso('Editar Unidad Negocio')}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default GestionUnidadNegocioSucursal;
